import React from "react";
import {
  MapPin,
  Briefcase,
  GraduationCap,
  Star,
  Eye,
  Share2,
  RefreshCw,
  Settings,
  BadgeAlert,
  MessageCircle,
  Copy,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { ListGridProps } from "../../../types";
import { InfoRow } from "./InfoRow";
import { useLocation } from "react-router-dom";
import {
  copyProfileId,
  getGenderPlaceholder,
} from "../../../helpers/matches.helper";
import { timeAgo } from "../../../helpers/function.helper";
import { UPLOAD_URL } from "../../../utils/constants";
import { useLanguage } from "../../../context/LanguageContext";
import {
  getPrivacyOverlayLabel,
  shouldBlurProfileImage,
} from "../../../utils/imagePrivacy";
import { isAccountSuspended } from "../../../utils/bureau";

const formatCreatedBy = (value?: string) => {
  if (!value) return "";
  return value.replace(/_/g, " ").replace(/\b\w/, (char) => char.toUpperCase());
};

export const GridView: React.FC<ListGridProps> = ({
  matches,
  setProfiles,
  onViewProfile,
  onManageProfile,
  onAddToShortlist,
  onSwitchProfile,
  onShareProfile,
  onReportProfile,
  getExtraActions,
  onTakeProfile,
  onWhatsAppProfile,
  renderActions,
}) => {
  const location = useLocation();
  const isOtherPage = location.pathname.includes("other");
  const isDirectPage = location.pathname.includes("/direct");
  const { language, t } = useLanguage();
  const scoreLabel = t("matches.labels.score");
  const publicLabel = t("matches.labels.public");
  const viewsTemplate = t("matches.labels.viewsCount");
  const lastSeenPrefix = t("matches.labels.lastSeen");
  const byLabel = t("matches.labels.by");
  const viewLabel = t("matches.buttons.view");
  const switchLabel = t("matches.buttons.switch");
  const manageLabel = t("matches.buttons.manage");
  const shareLabel = t("matches.buttons.share");
  const reportLabel = t("matches.buttons.report");
  const takeProfileLabel = t("Take This Profile");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {matches.map((match) => {
        const viewsLabel = viewsTemplate.replace(
          "{count}",
          String(match.views ?? 0),
        );
        const lastSeenLabel = `${lastSeenPrefix}: ${timeAgo(
          match.lastSeenAt,
          language,
        )}`;
        const isShortlisted = Boolean(match.isShortlisted);
        const blurPhoto = shouldBlurProfileImage(match.imagePrivacy);
        const isSuspended = isAccountSuspended(match.status);
        const handleCardKeyDown = (
          event: React.KeyboardEvent<HTMLDivElement>,
        ) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onViewProfile?.(match);
          }
        };

        return (
          <div key={match.id} className="space-y-3">
            <div
              className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden group cursor-pointer focus:outline-none"
              onClick={() => onViewProfile?.(match)}
              role="button"
              tabIndex={0}
              onKeyDown={handleCardKeyDown}
            >
              {isSuspended && (
                <div className="px-3 py-2 text-xs font-semibold text-red-700 bg-red-50 border-b border-red-100">
                  Profile is suspended
                </div>
              )}
              <div className="relative h-80 overflow-hidden">
                <img
                  src={UPLOAD_URL + "/users/profile_pic/" + match.image}
                  alt={match.fullName}
                  className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 object-top ${
                    blurPhoto ? "blur-sm scale-110 brightness-90" : ""
                  }`}
                  onError={(event) => {
                    (event.currentTarget as HTMLImageElement).src =
                      getGenderPlaceholder(match.gender);
                  }}
                />
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onAddToShortlist?.(match, matches, setProfiles);
                  }}
                  aria-label="Shortlist profile"
                  className={`absolute top-2 left-2 flex items-center justify-center rounded-full p-1.5 shadow-md transition ${
                    isShortlisted
                      ? "bg-orange-500 text-white hover:bg-orange-600"
                      : "bg-white/90 text-orange-500 hover:bg-white"
                  }`}
                >
                  {isShortlisted ? (
                    <Star className="w-4 h-4" fill="currentColor" />
                  ) : (
                    <Star className="w-4 h-4" />
                  )}
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <h3 className="text-xl font-bold text-white">{match.fullName}</h3>
                  <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-white/90">
                    <span className="truncate">ID: {match.maritalId}</span>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void copyProfileId(match.maritalId);
                      }}
                      aria-label="Copy profile ID"
                      title="Copy profile ID"
                      className="rounded bg-white/15 p-1 text-white transition hover:bg-white/30"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                  <p className="text-sm text-white/90">
                    {match.age}, {match.height}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 mb-2 px-4 pt-5">
                <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                  {scoreLabel}: {match.profileScore}%
                </span>
                <div className="flex items-center gap-1.5 bg-green-100 px-2 py-1 rounded-full">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>

                  <span className="text-xs font-semibold text-green-700">
                    {publicLabel}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-green-600">
                    <Eye size={16} />
                    <span className="text-sm font-semibold">{viewsLabel}</span>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-700 leading-relaxed space-y-1 px-4">
                <p className="flex flex-wrap gap-x-2 gap-y-1">
                  {match.maritalStatus && <span>{match.maritalStatus}</span>}
                  {match.caste && <span>{match.caste}</span>}
                  {match.annualIncome && <span>{match.annualIncome}</span>}
                </p>
              </div>
              <div className="px-4">
                <div className="space-y-2 mb-4">
                  {/* @ts-ignore */}
                  <InfoRow icon={Briefcase} text={match.profession} compact />
                  {/* @ts-ignore */}
                  <InfoRow
                    // @ts-ignore
                    icon={GraduationCap}
                    text={match?.education ?? ""}
                    compact
                  />
                  {/* @ts-ignore */}
                  <InfoRow icon={MapPin} text={match.location} compact />
                </div>

                <div className="text-xs text-gray-500 flex justify-between items-center">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onViewProfile?.(match);
                    }}
                    className="hover:text-purple-600 hover:underline"
                  >
                    {byLabel}: {formatCreatedBy(match.createdBy)}
                  </button>
                  <span>{lastSeenLabel}</span>
                </div>
              </div>
              <div className="p-2 border-t border-gray-100 space-y-2">
                {renderActions ? (
                  renderActions(match)
                ) : isDirectPage ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onTakeProfile?.(match);
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-green-600 text-white hover:bg-green-700 transition"
                  >
                    <MessageCircle size={16} />
                    <span className="font-semibold text-sm">
                      {takeProfileLabel}
                    </span>
                  </button>
                ) : (
                  <>
                    <div
                      className={`grid gap-2 ${
                        isOtherPage ? "grid-cols-3" : "grid-cols-3"
                      }`}
                    >
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          onViewProfile?.(match);
                        }}
                        className="w-full min-w-0 flex items-center justify-center gap-1.5 py-2 px-1 rounded-lg border border-purple-200 text-purple-600 hover:bg-purple-50 transition"
                      >
                        <Eye size={16} className="shrink-0" />
                        <span className="text-sm font-semibold truncate">
                          {viewLabel}
                        </span>
                      </button>

                      {!isOtherPage && (
                        <div className="relative min-w-0">
                          <button
                            className="w-full min-w-0 flex items-center justify-center gap-1.5 py-2 px-1 rounded-lg border border-purple-200 text-purple-600 hover:bg-purple-50 transition"
                            onClick={(event) => {
                              event.stopPropagation();
                              onSwitchProfile(match);
                            }}
                          >
                            <RefreshCw size={16} className="shrink-0" />
                            <span className="text-sm font-semibold truncate">
                              {switchLabel}
                            </span>
                          </button>

                          {match?.switchCount ? (
                            <span className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/2 inline-flex items-center justify-center min-w-[18px] h-5 px-1 rounded-full bg-red-500 text-[10px] font-semibold text-white">
                              {match.switchCount}
                            </span>
                          ) : null}
                        </div>
                      )}

                      <button
                        className="w-full min-w-0 flex items-center justify-center gap-1.5 py-2 px-1 rounded-lg border border-purple-200 text-purple-600 hover:bg-purple-50 transition"
                        onClick={(event) => {
                          event.stopPropagation();
                          onShareProfile?.(match);
                        }}
                      >
                        <Share2 size={16} className="shrink-0" />
                        <span className="text-sm font-semibold truncate">
                          {shareLabel}
                        </span>
                      </button>

                      {isOtherPage && (
                        <button
                          className="w-full min-w-0 flex items-center justify-center gap-1.5 py-2 px-1 rounded-lg border border-red-400 bg-red-400 text-white hover:bg-red-500 transition"
                          onClick={(event) => {
                            event.stopPropagation();
                            onReportProfile?.(match);
                          }}
                        >
                          <BadgeAlert size={16} className="shrink-0" />
                          <span className="text-sm font-semibold truncate">
                            {reportLabel}
                          </span>
                        </button>
                      )}
                    </div>

                    {!isOtherPage && (
                      <div
                        className={
                          onWhatsAppProfile ? "grid grid-cols-2 gap-2" : ""
                        }
                      >
                        <button
                          className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-orange-100 border border-orange-300 text-orange-700 hover:bg-orange-200 transition"
                          onClick={(event) => {
                            event.stopPropagation();
                            onManageProfile(match);
                          }}
                        >
                          <Settings size={16} />
                          <span>{manageLabel}</span>
                        </button>
                        {onWhatsAppProfile && (
                          <button
                            className="w-full min-w-0 flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-green-500 text-white hover:bg-green-600 transition"
                            onClick={(event) => {
                              event.stopPropagation();
                              onWhatsAppProfile(match);
                            }}
                          >
                            <FaWhatsapp size={16} className="shrink-0" />
                            <span className="truncate">WhatsApp</span>
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
